import { StateCreator } from 'zustand';

import {
  isLoneWall,
  isInsideSector,
  isPointOnLine,
  isSamePoint,
  isSharedWall,
  traceSectorWallRings,
  convertMeasurementToCm,
} from '@/common/planningUtils';
import { generateRoomPartId } from '@/common/utils';
import { PLANNER_HISTORY_MAX_LENGTH } from '@/components/Planner/PlannerConstants';

import { Wall, Sector } from '@/api/airchef/Api';
import { PlanSlice } from '@/types/stores/plan';
import { PlannerSlice } from '@/types/stores/planner';
import { ProjectSlice } from '@/types/stores/project';
import { RootActions } from '@/types/stores/rootActions';
import { BuildSlice } from '@/types/stores/build';
import { PlannerCorner, PlannerSector, PlannerWall } from '@/types/planner';
import { Vector2d } from 'konva/lib/types';
import Decimal from 'decimal.js';

export const createBuildSlice: StateCreator<
  BuildSlice & PlanSlice & PlannerSlice & ProjectSlice & RootActions,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  BuildSlice
> = (set, get) => ({
  build: {
    session: '',
    corners: {},
    walls: {},
    sectors: {},
    history: [],
    cornersList: () => {
      return Object.values(get().build.corners);
    },
    wallsList() {
      return Object.values(get().build.walls);
    },
    addCorner: (corner, height) => {
      const { getCorner, wallsList, corners, splitWall } = get().build;
      const defaultHeight = get().planner.prefs.defaultWallHeight / 10; // mm to cm
      const existing = getCorner(corner);
      if (existing) return existing;

      const id = generateRoomPartId();
      const newCorner = { ...corner, id, height: height ?? defaultHeight, z: 0 };

      set(
        (state) => {
          state.build.corners[id] = newCorner;
        },
        undefined,
        {
          type: 'build/addCorner',
          corner,
        },
      );

      // Check if the new corner is on top of any existing wall.
      // If it is, split that wall in to two.
      wallsList().forEach((wall) => {
        if (isLoneWall(wall)) {
          // Prevent splitting walls that are not in sectors yet.
          // That is, they are part of the sector currently being drawn.
          return;
        }
        const isOnWall = isPointOnLine(newCorner, corners[wall.start]!, corners[wall.end]!);
        if (isOnWall) {
          splitWall(newCorner, wall);
        }
      });

      return newCorner;
    },
    moveCorner: (cid, newPos) => {
      const { corners, getCorner } = get().build;
      if (!corners[cid]) {
        return;
      }

      const toMerge = getCorner(newPos);

      if (toMerge) {
        // Check if the merge would result in a wall ring with less than three walls.
        // i.e. no area, and block it.

        const { walls } = get().build;
        const rings = getRingsForCorner(toMerge, walls);
        const illegalMerge = rings.some((ring) => ring.wallIds.length < 4);

        if (illegalMerge) {
          return;
        }
      }

      set(
        (state) => {
          const { id, ...oldPos } = state.build.corners[cid];

          const mergeData: {
            affectedWalls: Wall[];
            affectedSectors: Sector[];
            deletedCorner: PlannerCorner | undefined;
          } = {
            affectedSectors: [],
            affectedWalls: [],
            deletedCorner: toMerge,
          };

          if (toMerge) {
            Object.values(state.build.walls).forEach((wall) => {
              if (wall.start === toMerge.id) {
                mergeData.affectedWalls.push({ ...wall });
                wall.start = id;
              }
              if (wall.end === toMerge.id) {
                mergeData.affectedWalls.push({ ...wall });
                wall.end = id;
              }

              const zeroLength = wall.end === wall.start;
              if (zeroLength) {
                wall.sectors.forEach((sid) => {
                  const sector = state.build.sectors[sid];
                  const newWalls = sector.walls.filter((w) => w !== wall.id);
                  if (newWalls.length !== sector.walls.length) {
                    mergeData.affectedSectors.push({ ...sector });
                  }
                  sector.walls = newWalls;

                  // Here, could also check if the sector is zero volume now and remove that, but that's another rabbit hole.
                  // Just trust the user not to be stupid about it ;)
                });
                delete state.build.walls[wall.id];
              }
            });

            delete state.build.corners[toMerge.id];
          }

          state.build.corners[cid] = {
            id,
            ...oldPos,
            ...newPos,
          };

          state.build.history.push({
            type: 'CORNER_MOVE',
            id,
            oldPos,
            newPos: {
              ...oldPos,
              ...newPos,
            },
            mergeData: toMerge ? mergeData : undefined,
          });
        },
        undefined,
        { type: 'build/moveCorner', corner: cid, newPos },
      );

      return get().build.corners[cid];
    },
    moveCorners: (newPoints) => {
      set(
        (state) => {
          const oldPositions: Record<string, Vector2d> = {};
          const newPositions: Record<string, Vector2d> = {};

          newPoints.forEach(({ id, x, y }) => {
            const corner = state.build.corners[id];
            if (!corner) {
              return;
            }

            oldPositions[id] = { x: corner.x, y: corner.y };
            newPositions[id] = { x, y };

            corner.x = x;
            corner.y = y;
          });

          if (Object.keys(newPositions).length < 1) {
            return;
          }

          state.build.history.push({
            type: 'CORNERS_MOVE',
            oldPositions,
            newPositions,
          });
        },
        undefined,
        { type: 'build/moveCorners', corners: newPoints },
      );
    },
    splitWall: (corner, wall) => {
      const leftWall = {
        ...wall,
        id: generateRoomPartId(),
        sectors: [...wall.sectors],
      };

      const rightWall = {
        ...wall,
        id: generateRoomPartId(),
        sectors: [...wall.sectors],
      };

      leftWall.end = corner.id;
      rightWall.start = corner.id;

      const prevCornerId = leftWall.start;
      const nextCornerId = rightWall.end;

      set(
        (state) => {
          // Remove old wall
          delete state.build.walls[wall.id];
          // Add new walls
          state.build.walls[leftWall.id] = leftWall;
          state.build.walls[rightWall.id] = rightWall;

          // Update all sectors the wall is linked to
          for (const sector of wall.sectors) {
            // Find the index of the old wall
            const wallIndex = state.build.sectors[sector].walls.indexOf(wall.id);
            // Replace the old wall with both of the new walls
            state.build.sectors[sector].walls.splice(wallIndex, 1, leftWall.id, rightWall.id);
          }

          // Calculate the new corner height
          const prevCorner = state.build.corners[prevCornerId];
          const nextCorner = state.build.corners[nextCornerId];

          const newCornerHeight = new Decimal((prevCorner.height + nextCorner.height) / 2)
            .toDecimalPlaces(6)
            .toNumber();

          state.build.corners[corner.id].height = newCornerHeight;

          state.build.history.push({ type: 'WALL_SPLIT', removedWall: wall, addedWalls: [leftWall.id, rightWall.id] });
        },
        undefined,
        {
          type: 'build/splitWall',
          oldWall: wall.id,
          firstWall: leftWall.id,
          rightWall: rightWall.id,
        },
      );
      return [leftWall, rightWall];
    },
    getCorner: (corner) => {
      const { cornersList } = get().build;
      return cornersList().find((point) => isSamePoint(corner, point));
    },
    addWall: (startPoint, endPoint, height) => {
      const { addCorner, getWall } = get().build;

      const heightVal = height ? (typeof height === 'number' ? height : convertMeasurementToCm(height)) : undefined;

      const start = addCorner(startPoint, heightVal);
      const end = addCorner(endPoint, heightVal ?? start.height);

      const existingWall = getWall(start, end);
      if (existingWall) return existingWall;

      const id = generateRoomPartId();
      const newWall: Wall = { id, start: start.id, end: end.id, sectors: [] };

      set(
        (state) => {
          state.build.walls[id] = newWall;
          state.build.history.push({ type: 'WALL_ADD', wall: id });
        },
        undefined,
        {
          type: 'build/addWall',
          wall: id,
        },
      );

      return newWall;
    },
    getWall: (startPoint, endPoint) => {
      const { walls, getCorner } = get().build;
      const startCorner = (startPoint as PlannerCorner).id ? (startPoint as PlannerCorner) : getCorner(startPoint);
      const endCorner = (endPoint as PlannerCorner).id ? (endPoint as PlannerCorner) : getCorner(endPoint);
      if (!startCorner || !endCorner) {
        return undefined;
      }
      const existingWall = Object.values(walls).find(
        (wall) =>
          (wall.start === startCorner.id && wall.end === endCorner.id) ||
          (wall.start === endCorner.id && wall.end === startCorner.id),
      );

      return existingWall;
    },
    removeWalls: (walls) => {
      set(
        (state) => {
          const removedWalls: PlannerWall[] = [];
          const affectedSectors: PlannerSector[] = [];
          const sectorSet = new Set<string>();
          // Build a set of all affected sectors, deduping
          // And collect the walls to be removed
          for (const wid of walls) {
            const wall = state.build.walls[wid];
            if (!wall) continue;

            removedWalls.push({ ...wall });

            for (const sid of wall.sectors) {
              sectorSet.add(sid);
            }
          }
          // Update the affected sectors by removing the references to removed walls
          // and collect the sector data for undo history
          for (const sid of sectorSet) {
            const sector = state.build.sectors[sid];
            if (!sector) continue;

            affectedSectors.push({ ...sector, walls: [...sector.walls] });

            sector.walls = sector.walls.filter((sw) => !walls.includes(sw));
          }
          // Remove the walls
          for (const wid of walls) {
            delete state.build.walls[wid];
          }

          const removedCorners: Record<string, PlannerCorner> = removeOrphanedCorners(
            state.build.corners,
            state.build.walls,
          );

          state.build.history.push({
            type: 'WALL_REMOVE',
            removedWalls,
            affectedSectors,
            removedCorners,
          });
        },
        undefined,
        { type: 'build/removeWalls', walls },
      );
    },
    addSector: (walls) => {
      const { walls: oldWalls, sectors: oldSectors, corners } = get().build;
      // Check if any of the walls already have a sector
      const firstSharedwall = walls.find((wid) => oldWalls[wid].sectors.length > 0);

      let insideSectorId: string | undefined;
      if (!firstSharedwall) {
        // No shared walls, check if this sector is completely inside another
        const newSectCorners = walls.map((w) => oldWalls[w]).map((w) => corners[w.start]);
        const insideSector = Object.values(oldSectors).find((sect) =>
          newSectCorners.every((corner) => isInsideSector(corner, sect.walls, oldWalls, corners)),
        );
        if (insideSector) {
          insideSectorId = insideSector.id;
        }
      }

      if (insideSectorId) {
        set(
          (state) => {
            const s = state.build.sectors[insideSectorId];
            s.walls = [...s.walls, ...walls];
            const newWalls = { ...state.build.walls };
            walls.forEach((wallId) => {
              newWalls[wallId].sectors = [...newWalls[wallId].sectors, s.id];
            });
            state.build.walls = newWalls;
            state.build.history.push({ type: 'SECTOR_ADD_INSIDE', sector: s.id, walls: walls });
          },
          undefined,
          { type: 'build/addSector', inside: true },
        );

        return get().build.sectors[insideSectorId];
      } else {
        const id = generateRoomPartId();
        const sector: Sector = { id, walls };

        set(
          (state) => {
            state.build.sectors[id] = sector;
            state.build.history.push({ type: 'SECTOR_ADD', sector: id });
            const newWalls = { ...state.build.walls };
            walls.forEach((wallId) => {
              newWalls[wallId].sectors = [...newWalls[wallId].sectors, id];
            });
            state.build.walls = newWalls;
          },
          undefined,
          { type: 'build/addSector', sector: id, inside: false },
        );

        return sector;
      }
    },
    removeSector: (sid) => {
      const sector = get().build.sectors[sid];
      if (!sector) {
        console.error('no sector found with id!');
        return;
      }

      const sectorCount = Object.keys(get().build.sectors).length;

      if (sectorCount < 2) return;

      set(
        (state) => {
          const affectedWalls: Record<string, Wall> = {};

          // Go through all the walls in the sector and update them as needed
          state.build.sectors[sid].walls.forEach((wid) => {
            // Copy the original wall to the affected list before any changes,
            // so we can use it in undo later.
            affectedWalls[wid] = { ...state.build.walls[wid] };
            const wall = state.build.walls[wid];
            if (isSharedWall(wall)) {
              // If the wall is shared, remove current sector from the wall's sectors
              wall.sectors = wall.sectors.filter((ws) => ws !== sid);
            } else {
              // If the wall is not shared, remove it completely
              delete state.build.walls[wid];
            }
          });

          // Remove leftover corners from the removed walls
          const removedCorners: Record<string, PlannerCorner> = removeOrphanedCorners(
            state.build.corners,
            state.build.walls,
          );

          // remove the sector itself
          const removedSector = { ...state.build.sectors[sid] };
          delete state.build.sectors[sid];

          state.build.history.push({
            type: 'SECTOR_REMOVE',
            sector: removedSector,
            walls: affectedWalls,
            corners: removedCorners,
          });
        },
        undefined,
        { type: 'build/removeSector', sector: sid },
      );
    },
    getRenderableCorners: () => {
      return Object.values(get().build.corners);
    },
    getRenderableWalls: <T extends boolean>(
      plain: T,
    ): T extends true ? [Wall, number[]][] : [Wall, [PlannerCorner, PlannerCorner]][] => {
      const { walls, corners } = get().build;
      return Object.values(walls).map((wall) =>
        plain
          ? [wall, [corners[wall.start].x, corners[wall.start].y, corners[wall.end].x, corners[wall.end].y]]
          : [wall, [corners[wall.start], corners[wall.end]]],
      ) as T extends true ? [Wall, number[]][] : [Wall, [PlannerCorner, PlannerCorner]][];
    },
    getRenderableSectors: () => {
      const { sectors, corners, walls } = get().build;
      const sectorList: [PlannerSector, PlannerCorner[][]][] = [];

      Object.values(sectors).forEach((sector) => {
        const rings = traceSectorWallRings(sector.walls, walls);
        const sectorCorners = rings.map((ring) =>
          ring.cornerIds
            .map((cornerId) => corners[cornerId])
            .filter((corner): corner is PlannerCorner => Boolean(corner)),
        );

        if (sectorCorners.some((ring) => ring.length > 0)) {
          sectorList.push([sector, sectorCorners]);
        }
      });

      return sectorList;
    },
    getCorners: () => {
      return Object.entries(get().build.corners);
    },
    removeOrphanedCorners: () => {
      const { walls, corners } = get().build;

      // This gets modified in place so we can apply it directly to the state
      const newCorners: Record<string, PlannerCorner> = { ...corners };
      const removedCorners = removeOrphanedCorners(newCorners, walls);

      set(
        (state) => {
          state.build.corners = newCorners;
        },
        undefined,
        { type: 'build/removeOrphanedCorners' },
      );

      return removedCorners;
    },
    removeUnattachedWalls: () => {
      set(
        (state) => {
          Object.values(state.build.walls).forEach((wall) => {
            // Filter out sector attachments from this wall to sectors that do not exist
            wall.sectors = wall.sectors.filter((sid) => !!state.build.sectors[sid]);

            // If there are no sectors attached, remove the wall
            if (wall.sectors.length < 1) {
              delete state.build.walls[wall.id];
            }
          });
        },
        undefined,
        { type: 'build/removeUnattachedWalls' },
      );
    },
    updateHeights: (corners, newHeight, connected) => {
      const cornersToUpdate = new Set(corners);
      if (connected) {
        const { walls } = get().build;
        const visitedWalls = new Set<string>();
        const allWalls = Object.values(walls);
        let currentWall: Wall | undefined = undefined;
        do {
          currentWall = allWalls.find(
            (w) => !visitedWalls.has(w.id) && (cornersToUpdate.has(w.start) || cornersToUpdate.has(w.end)),
          );

          if (!currentWall) break;

          cornersToUpdate.add(currentWall.start);
          cornersToUpdate.add(currentWall.end);

          visitedWalls.add(currentWall.id);
        } while (currentWall);
      }

      set(
        (state) => {
          for (const corner of cornersToUpdate) {
            state.build.corners[corner].height = newHeight;
          }
        },
        undefined,
        { type: 'build/updateHeights', corners, newHeight, updateConnected: connected },
      );
    },
    undo: (type) => {
      const { history } = get().build;
      let newHistory = [...history];
      const lastItem = type ? newHistory.findLast((item) => item.type === type) : newHistory.pop();

      if (type) {
        newHistory = newHistory.filter((val) => val !== lastItem);
      }

      if (!lastItem) {
        return;
      }

      switch (lastItem.type) {
        case 'WALL_ADD': {
          const newWalls = { ...get().build.walls };
          delete newWalls[lastItem.wall];
          set(
            (state) => {
              state.build.history = newHistory;
              state.build.walls = newWalls;
            },
            undefined,
            { type: 'build/undo', item: lastItem.type, wall: lastItem.wall },
          );
          get().build.removeOrphanedCorners();
          return;
        }
        case 'WALL_SPLIT': {
          set(
            (state) => {
              // Update the global wall list, remove the added walls
              lastItem.addedWalls.forEach((w) => delete state.build.walls[w]);
              // Add the original wall back in
              state.build.walls[lastItem.removedWall.id] = lastItem.removedWall;

              // Update all affected sectors
              const sectors = lastItem.removedWall.sectors;
              for (const sid of sectors) {
                // Check at which point the walls were instered in the wall list
                const splicePoint = state.build.sectors[sid].walls.indexOf(lastItem.addedWalls[0]);
                // Remove the split walls
                state.build.sectors[sid].walls = state.build.sectors[sid].walls.filter(
                  (wid) => wid !== lastItem.addedWalls[0] && wid !== lastItem.addedWalls[1],
                );
                // Add the original wall back to the original position
                state.build.sectors[sid].walls.splice(splicePoint, 0, lastItem.removedWall.id);
              }

              state.build.history = newHistory;
            },
            undefined,
            {
              type: 'build/undo',
              item: lastItem.type,
              splitWalls: lastItem.addedWalls,
              originalWall: lastItem.removedWall.id,
            },
          );

          get().build.removeOrphanedCorners();
          return;
        }
        case 'WALL_REMOVE': {
          set(
            (state) => {
              for (const wall of lastItem.removedWalls) {
                state.build.walls[wall.id] = wall;
              }

              for (const sector of lastItem.affectedSectors) {
                state.build.sectors[sector.id] = sector;
              }

              state.build.corners = {
                ...state.build.corners,
                ...lastItem.removedCorners,
              };

              state.build.history = newHistory;
            },
            undefined,
            {
              type: 'build/undo',
              item: lastItem.type,
              walls: lastItem.removedWalls,
            },
          );
          return;
        }
        case 'SECTOR_ADD': {
          set(
            (state) => {
              const sectorId = lastItem.sector;
              const newWalls = { ...state.build.walls };
              const newSectors = { ...state.build.sectors };

              // Go through all the walls
              state.build.sectors[sectorId].walls.forEach((wallId) => {
                const wall = newWalls[wallId];
                if (!isSharedWall(wall)) {
                  // Remove walls where this sector was the only linked sector
                  delete newWalls[wallId];
                  newHistory = newHistory.filter((item) => item.type !== 'WALL_ADD' || item.wall !== wallId);
                } else {
                  wall.sectors = wall.sectors.filter((id) => id !== sectorId);
                }
              });

              delete newSectors[sectorId];

              state.build.walls = newWalls;
              state.build.sectors = newSectors;
              state.build.history = newHistory;
            },
            undefined,
            { type: 'build/undo', item: lastItem.type, sector: lastItem.sector },
          );

          get().build.removeOrphanedCorners();
          return;
        }
        case 'SECTOR_ADD_INSIDE': {
          set(
            (state) => {
              const addedWalls = lastItem.walls;

              const newWalls = { ...state.build.walls };
              addedWalls.forEach((wid) => {
                delete newWalls[wid];
                newHistory = newHistory.filter((item) => item.type !== 'WALL_ADD' || item.wall !== wid);
              });

              state.build.walls = newWalls;
              state.build.history = newHistory;
            },
            undefined,
            { type: 'build/undo', item: lastItem.type, sector: lastItem.sector, walls: lastItem.walls },
          );
          get().build.removeOrphanedCorners();
          return;
        }
        case 'SECTOR_REMOVE': {
          const { type, ...rest } = lastItem;
          set(
            (state) => {
              state.build.sectors[lastItem.sector.id] = lastItem.sector;

              state.build.corners = { ...state.build.corners, ...lastItem.corners };
              state.build.walls = { ...state.build.walls, ...lastItem.walls };

              state.build.history = newHistory;
            },
            undefined,
            { type: 'build/undo', item: type, ...rest },
          );
          return;
        }
        case 'CORNER_MOVE': {
          const { id, oldPos, mergeData } = lastItem;

          set(
            (state) => {
              if (state.build.corners[id]) {
                state.build.corners[id] = {
                  id,
                  ...oldPos,
                };
              }

              if (mergeData) {
                mergeData.affectedWalls.forEach((wall) => {
                  state.build.walls[wall.id] = wall;
                });
                mergeData.affectedSectors.forEach((sector) => {
                  state.build.sectors[sector.id] = sector;
                });
                if (mergeData.deletedCorner) {
                  state.build.corners[mergeData.deletedCorner.id] = mergeData.deletedCorner;
                }
              }

              state.build.history = newHistory;
            },
            undefined,
            { type: 'build/undo', item: type, corner: id },
          );
          return;
        }
        case 'CORNERS_MOVE': {
          const { oldPositions } = lastItem;

          set(
            (state) => {
              Object.entries(oldPositions).forEach(([id, pos]) => {
                const corner = state.build.corners[id];
                if (!corner) {
                  return;
                }
                corner.x = pos.x;
                corner.y = pos.y;
              });

              state.build.history = newHistory;
            },
            undefined,
            { type: 'build/undo', item: lastItem.type, corners: Object.keys(oldPositions) },
          );
          return;
        }
        default: {
          const { type } = lastItem satisfies never;
          console.warn(`Unknown build undo item type ${type}!`);
        }
      }
    },
    trimHistory: () => {
      if (get().build.history.length > PLANNER_HISTORY_MAX_LENGTH) {
        set(
          (state) => {
            while (state.build.history.length > PLANNER_HISTORY_MAX_LENGTH) {
              state.build.history.shift();
            }
          },
          undefined,
          { type: 'build/trimHistory' },
        );
      }
    },
    getLatestWall: () => {
      const lastAdd = get().build.history.findLast((item) => item.type === 'WALL_ADD');
      if (!lastAdd) {
        return null;
      }

      return get().build.walls[lastAdd.wall];
    },
    clear: () => {
      set(
        (state) => {
          state.build.session = '';
          state.build.corners = {};
          state.build.walls = {};
          state.build.sectors = {};
          state.build.history = [];
        },
        undefined,
        { type: 'build/clear' },
      );
    },
  },
});

/**
 * Removes any orphaned corners from the corners record, updating it in place.
 * Return a Record of removed corners
 * @param corners Record of corner ids and points
 * @param walls Record of wall ids and walls
 * @returns
 */
function removeOrphanedCorners(
  corners: Record<string, PlannerCorner>,
  walls: Record<string, Wall>,
): Record<string, PlannerCorner> {
  // Build a list of all the corners that are used in walls
  const usedCorners: string[] = Object.values(walls).reduce((foundCorners, wall) => {
    if (!foundCorners.includes(wall.start)) {
      foundCorners.push(wall.start);
    }
    if (!foundCorners.includes(wall.end)) {
      foundCorners.push(wall.end);
    }
    return foundCorners;
  }, [] as string[]);

  const cornerList = Object.keys(corners);

  const removedCorners: Record<string, PlannerCorner> = {};

  // Remove corners that were not attached to walls
  cornerList.forEach((corner) => {
    if (!usedCorners.includes(corner)) {
      removedCorners[corner] = corners[corner];
      delete corners[corner];
    }
  });

  return removedCorners;
}

function getRingsForCorner(source: Readonly<PlannerCorner>, walls: Readonly<Record<string, PlannerWall>>) {
  const wallsList = Object.values(walls);
  const wallsWithCorner = wallsList.filter((w) => w.start === source.id || w.end === source.id);

  const adjacentSectors = new Set(wallsWithCorner.map((w) => w.sectors).flat(2));
  const sectorWalls = wallsList.filter((w) => new Set(w.sectors).intersection(adjacentSectors).size > 0);
  const sectorWallIds = sectorWalls.map((w) => w.id);

  const rings = traceSectorWallRings(sectorWallIds, walls);
  return rings;
}
